import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Switch,
  Chip,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Launch as LaunchIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Layers as LayersIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpOutlineIcon,
  AccountTree as AccountTreeIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  fetchFestivals,
  toggleFestivalSaaS,
  fetchFestivalArchitecture,
  toggleFestivalModule,
  updateFestivalSettings,
  getFestivalLaunchToken,
} from '../../services/saasAccessService';

// Domain icon mapping for tree visualization
const DOMAIN_ICON_MAP = {
  submission: '🎬',
  submissions: '🎬',
  review: '⚖️',
  reviews: '⚖️',
  jury: '⚖️',
  award: '🏆',
  awards: '🏆',
  team: '🛡️',
  user: '👥',
  users: '👥',
  task: '📋',
  tasks: '📋',
  schedule: '🗓️',
  calendar: '🗓️',
  guest: '🛎️',
  guests: '🛎️',
  chat: '💬',
  dashboard: '📊',
  sponsor: '🤝',
  sponsors: '🤝',
  comms: '📢',
  payout: '💳',
  payouts: '💳',
  setting: '⚙️',
  settings: '⚙️',
  audience: '🎟️',
  attendance: '📋',
  voting: '🗳️',
  registration: '🎫',
};

const GROUP_METAS = {
  admin: {
    label: 'Admin',
    icon: '🛡️',
    desc: 'Festival Director & Control Tower governance (Full Access)',
  },
  jury: {
    label: 'Jury',
    icon: '⚖️',
    desc: 'Screening reviews, scorecards, and jury ballots',
  },
  judge: {
    label: 'Jury',
    icon: '⚖️',
    desc: 'Screening reviews, scorecards, and jury ballots',
  },
  volunteer: {
    label: 'Volunteer',
    icon: '📋',
    desc: 'Shift operations, task execution, and check-in desk',
  },
};

/**
 * Helper: Group scoped permissions by domain prefix (e.g. 'submission', 'review', etc.)
 */
function groupScopedPermissionsByDomain(scopedPermissions, fallbackPrefix = 'general') {
  const map = new Map();
  for (const p of scopedPermissions) {
    let domain = fallbackPrefix;
    if (p.key && p.key.includes(':')) {
      domain = p.key.split(':')[0];
    }
    if (!map.has(domain)) {
      map.set(domain, {
        domain,
        icon: DOMAIN_ICON_MAP[domain] || '📁',
        permissions: [],
      });
    }
    map.get(domain).permissions.push(p);
  }
  return Array.from(map.values());
}

// SafeChip: wraps MUI Chip to guarantee onClick is never undefined, preventing "onClick is not a function" errors
const SafeChip = React.forwardRef(function SafeChip({ onClick, sx, ...props }, ref) {
  return (
    <Chip
      ref={ref}
      onClick={typeof onClick === 'function' ? onClick : () => {}}
      sx={{
        ...(!onClick && { cursor: 'inherit' }),
        ...sx,
      }}
      {...props}
    />
  );
});

export default function FestivalAccessControl() {
  const [enabledFestivals, setEnabledFestivals] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFestivalId, setSelectedFestivalId] = useState(null);
  const [architecture, setArchitecture] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [archLoading, setArchLoading] = useState(false);
  const [matrixState, setMatrixState] = useState({}); // { [moduleId]: { [groupKey]: Set<shortKey> } }
  const [seatLimitInput, setSeatLimitInput] = useState(25);
  const [savingLimits, setSavingLimits] = useState(false);
  const [launchingPortal, setLaunchingPortal] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Expanded tree nodes: { [nodeKey]: boolean }
  const [expandedNodes, setExpandedNodes] = useState({});
  // Expanded module accordions: { [moduleId]: boolean }
  const [expandedModules, setExpandedModules] = useState({});

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // Load only enabled festivals on initial load
  const loadEnabledFestivals = useCallback(async (selectId = null) => {
    try {
      setLoading(true);
      const data = await fetchFestivals('');
      const list = data.festivals || [];
      setEnabledFestivals(list);

      const targetId = selectId || selectedFestivalId;
      if (targetId && list.some(f => f.event_id === targetId)) {
        setSelectedFestivalId(targetId);
      } else if (list.length > 0) {
        setSelectedFestivalId(list[0].event_id);
      } else {
        setSelectedFestivalId(null);
        setArchitecture(null);
      }
    } catch (err) {
      console.error('Failed to load active festivals:', err);
      showToast('Failed to load active festivals list', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedFestivalId]);

  // Load selected festival architecture
  const loadArchitecture = useCallback(async (eventId) => {
    if (!eventId) return;
    try {
      setArchLoading(true);
      const data = await fetchFestivalArchitecture(eventId);
      setArchitecture(data);
      setSeatLimitInput(data.edition?.maxUsers || 25);

      const initialMatrix = {};
      const initialExpanded = {};
      const initialExpandedModules = {};

      (data.modules || []).forEach(m => {
        initialMatrix[m.id] = {};
        initialExpandedModules[m.id] = m.type === 'core';

        (data.groups || []).forEach(g => {
          const keys = m.groupAccessMatrix?.[g.key] || [];
          initialMatrix[m.id][g.key] = new Set(keys);
          initialExpanded[`${m.id}:${g.key}`] = true; // Group branch expanded
        });

        const domains = groupScopedPermissionsByDomain(m.scopedPermissions || [], m.moduleKey);
        domains.forEach(d => {
          (data.groups || []).forEach(g => {
            initialExpanded[`${m.id}:${g.key}:${d.domain}`] = true; // Domain branch expanded
          });
        });
      });

      setMatrixState(initialMatrix);
      setExpandedNodes(initialExpanded);
      setExpandedModules(initialExpandedModules);
    } catch (err) {
      console.error('Failed to load architecture:', err);
      showToast('Failed to load festival architecture', 'error');
    } finally {
      setArchLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadEnabledFestivals();
  }, [loadEnabledFestivals]);

  // Load architecture when selection changes
  useEffect(() => {
    if (selectedFestivalId) {
      loadArchitecture(selectedFestivalId);
    }
  }, [selectedFestivalId, loadArchitecture]);

  // Handle live search with debounce
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setIsSearching(false);
      setSearchLoading(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchLoading(true);
    let isCurrent = true;

    const timer = setTimeout(async () => {
      try {
        const data = await fetchFestivals(trimmed);
        if (isCurrent) {
          setSearchResults(data.festivals || []);
        }
      } catch (err) {
        if (isCurrent) {
          console.error('Search error:', err);
        }
      } finally {
        if (isCurrent) {
          setSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Toggle SaaS status (enable or disable)
  const handleToggleSaaS = async (festival, e) => {
    e.stopPropagation();
    const eventId = festival.event_id;
    const nextStatus = !(festival.saas_enabled || festival.is_saas);

    try {
      await toggleFestivalSaaS(eventId, nextStatus);
      showToast(
        nextStatus
          ? `"${festival.name}" has been enabled for SaaS!`
          : `"${festival.name}" SaaS disabled.`
      );

      if (nextStatus) {
        setSelectedFestivalId(eventId);
        await loadEnabledFestivals(eventId);
        setSearchResults(prev =>
          prev.map(f => (f.event_id === eventId ? { ...f, saas_enabled: true, is_saas: true } : f))
        );
      } else {
        await loadEnabledFestivals();
        setSearchResults(prev =>
          prev.map(f => (f.event_id === eventId ? { ...f, saas_enabled: false, is_saas: false } : f))
        );
      }
    } catch (err) {
      console.error('Failed to toggle SaaS:', err);
      showToast('Failed to update festival SaaS status', 'error');
    }
  };

  // Toggle module provisioning for festival
  const handleToggleModule = async (moduleId, currentStatus) => {
    try {
      const nextStatus = !currentStatus;
      await toggleFestivalModule(selectedFestivalId, moduleId, nextStatus);
      showToast(`Module ${nextStatus ? 'provisioned' : 'disabled'}`);

      setArchitecture(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          modules: prev.modules.map(m => m.id === moduleId ? { ...m, isEnabled: nextStatus } : m)
        };
      });
    } catch (err) {
      console.error('Failed to toggle module:', err);
      showToast('Failed to update module provisioning', 'error');
    }
  };

  // Toggle a tree node expansion
  const handleToggleNode = (nodeKey) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeKey]: prev[nodeKey] === false ? true : false,
    }));
  };

  // Toggle individual module accordion
  const handleToggleModuleAccordion = (moduleId, isExpanded) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: isExpanded !== undefined ? isExpanded : !prev[moduleId],
    }));
  };

  // Expand / Collapse all modules at top level
  const handleSetAllModulesExpanded = (expand) => {
    if (!architecture?.modules) return;
    const next = {};
    architecture.modules.forEach(m => {
      next[m.id] = expand;
    });
    setExpandedModules(next);
  };

  // Expand / Collapse all groups and permission domain nodes inside a specific module
  const handleSetModuleGroupsExpanded = (moduleId, expand) => {
    if (!architecture) return;
    const mod = architecture.modules?.find(m => m.id === moduleId);
    if (!mod) return;

    setExpandedNodes(prev => {
      const next = { ...prev };
      (architecture.groups || []).forEach(g => {
        next[`${mod.id}:${g.key}`] = expand;
        const domains = groupScopedPermissionsByDomain(mod.scopedPermissions || [], mod.moduleKey);
        domains.forEach(d => {
          next[`${mod.id}:${g.key}:${d.domain}`] = expand;
        });
      });
      return next;
    });
  };

  // Global deep expand / collapse all modules AND all groups (available if needed)
  // eslint-disable-next-line no-unused-vars
  const handleSetAllDeepExpanded = (expand) => {
    if (!architecture) return;
    handleSetAllModulesExpanded(expand);
    const next = {};
    (architecture.modules || []).forEach(m => {
      (architecture.groups || []).forEach(g => {
        next[m.id + ':' + g.key] = expand;
        const domains = groupScopedPermissionsByDomain(m.scopedPermissions || [], m.moduleKey);
        domains.forEach(d => {
          next[m.id + ':' + g.key + ':' + d.domain] = expand;
        });
      });
    });
    setExpandedNodes(next);
  };

  // Save Seat Limits
  const handleSaveSeatLimits = async () => {
    try {
      setSavingLimits(true);
      await updateFestivalSettings(selectedFestivalId, { maxUsers: seatLimitInput });
      showToast(`Seat allocation limit updated to ${seatLimitInput} seats`);
      setArchitecture(prev => ({
        ...prev,
        edition: { ...prev.edition, maxUsers: seatLimitInput }
      }));
    } catch (err) {
      console.error('Failed to save seat limits:', err);
      showToast('Failed to update seat limit', 'error');
    } finally {
      setSavingLimits(false);
    }
  };

  // Launch Festival Portal with Admin SSO Impersonation
  const handleLaunchPortal = async () => {
    const eventId = architecture?.edition?.eventId;
    if (!eventId) return;

    // Open blank window immediately to avoid browser popup blockers
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.opener = null;
    }

    setLaunchingPortal(true);
    try {
      const portalBaseUrl =
        process.env.REACT_APP_FESTIVAL_PORTAL_URL ||
        (window.location.hostname.includes('autovertest.com')
          ? 'https://ffestiplus.autovertest.com'
          : 'https://ffestiplus.freecomers.com');

      const res = await getFestivalLaunchToken(eventId);
      let targetUrl;
      if (res?.token) {
        targetUrl = `${portalBaseUrl}/login?token=${encodeURIComponent(res.token)}&festival=${eventId}`;
      } else if (res?.launchUrl) {
        targetUrl = res.launchUrl;
      } else {
        targetUrl = `${portalBaseUrl}/dashboard?festival=${eventId}`;
      }

      if (newWindow) {
        newWindow.location.href = targetUrl;
      } else {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to generate launch token:', err);
      const portalBaseUrl =
        process.env.REACT_APP_FESTIVAL_PORTAL_URL ||
        (window.location.hostname.includes('autovertest.com')
          ? 'https://ffestiplus.autovertest.com'
          : 'https://ffestiplus.freecomers.com');
      const fallbackUrl = `${portalBaseUrl}/dashboard?festival=${eventId}`;
      if (newWindow) {
        newWindow.location.href = fallbackUrl;
      } else {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setLaunchingPortal(false);
    }
  };

  // Display list: if searching, show searchResults; otherwise show enabledFestivals
  const displayFestivals = isSearching ? searchResults : enabledFestivals;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1680, margin: '0 auto' }}>
      {/* Top Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <SecurityIcon sx={{ fontSize: 32, color: '#38bdf8' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
              Freecomers Platform Administration
            </Typography>
            <SafeChip
              label="SaaS & Access Control"
              size="small"
              sx={{ bgcolor: '#0284c7', color: '#fff', fontWeight: 600 }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Inspect granted permissions per group in Tree View. Permissions are immutable (read-only).
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SafeChip
            label={`${enabledFestivals.length} Active SaaS Festivals`}
            sx={{ bgcolor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', fontWeight: 700, border: '1px solid #38bdf8' }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setSearchQuery('');
              loadEnabledFestivals();
            }}
            sx={{
              color: '#f8fafc',
              borderColor: '#475569',
              '&:hover': { borderColor: '#94a3b8', bgcolor: 'rgba(255,255,255,0.05)' }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Main Two-Column Side-by-Side Flexbox Layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, width: '100%', alignItems: 'flex-start' }}>
        
        {/* ======================================================== */}
        {/* LEFT COLUMN: Active Festivals & Search to Enable SaaS     */}
        {/* ======================================================== */}
        <Box sx={{ width: { xs: '100%', lg: 390 }, flexShrink: 0 }}>
          <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
            
            {/* Column Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LayersIcon fontSize="small" sx={{ color: '#0284c7' }} />
                {isSearching ? 'Search Festivals' : 'Active SaaS Festivals'}
              </Typography>
              <SafeChip
                label={`${displayFestivals.length} ${isSearching ? 'results' : 'active'}`}
                size="small"
                variant="outlined"
                color={isSearching ? 'primary' : 'success'}
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {/* Universal Search Bar */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search festival name to enable SaaS..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <ClearIcon
                      fontSize="small"
                      sx={{ cursor: 'pointer', color: '#94a3b8', '&:hover': { color: '#0f172a' } }}
                      onClick={() => setSearchQuery('')}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Helpful Notice */}
            {!isSearching && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                }}
              >
                <HelpOutlineIcon sx={{ color: '#0284c7', fontSize: 18, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#0369a1', lineHeight: 1.3 }}>
                  Showing <b>only SaaS-enabled festivals</b>. Search above to find and enable any other festival.
                </Typography>
              </Box>
            )}

            {/* List of Festivals */}
            <Box sx={{ maxHeight: 'calc(100vh - 310px)', overflowY: 'auto', pr: 0.5 }}>
              {loading || searchLoading ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <CircularProgress size={28} />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748b' }}>
                    {searchLoading ? 'Searching festivals...' : 'Loading active festivals...'}
                  </Typography>
                </Box>
              ) : displayFestivals.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                    {isSearching
                      ? `No festivals matching "${searchQuery}" found.`
                      : 'No SaaS-enabled festivals yet. Use the search box above to find and enable a festival.'}
                  </Typography>
                </Box>
              ) : (
                displayFestivals.map((fest) => {
                  const isSelected = selectedFestivalId === fest.event_id;
                  const isEnabled = Boolean(fest.saas_enabled || fest.is_saas);

                  return (
                    <Card
                      key={fest.event_id}
                      variant="outlined"
                      onClick={() => setSelectedFestivalId(fest.event_id)}
                      sx={{
                        mb: 1.5,
                        cursor: 'pointer',
                        borderRadius: 2,
                        transition: 'all 0.18s ease',
                        borderWidth: isSelected ? 2 : 1,
                        borderColor: isSelected ? '#0284c7' : '#e2e8f0',
                        bgcolor: isSelected ? '#f0f9ff' : '#ffffff',
                        boxShadow: isSelected ? '0 2px 8px rgba(2, 132, 199, 0.12)' : 'none',
                        '&:hover': {
                          borderColor: isSelected ? '#0284c7' : '#cbd5e1',
                          bgcolor: isSelected ? '#f0f9ff' : '#f8fafc',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: isSelected ? '#0369a1' : '#0f172a',
                                lineHeight: 1.3,
                                mb: 0.5,
                              }}
                            >
                              {fest.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {fest.edition || 'Active Edition'}
                              </Typography>
                              {isEnabled ? (
                                <SafeChip
                                  label="SaaS Active"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor: '#dcfce7',
                                    color: '#166534',
                                    pointerEvents: 'none',
                                  }}
                                />
                              ) : (
                                <SafeChip
                                  label="Inactive"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    bgcolor: '#f1f5f9',
                                    color: '#64748b',
                                    pointerEvents: 'none',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Tooltip title={isEnabled ? 'Click to Disable SaaS' : 'Click to Enable SaaS'}>
                              <Switch
                                size="small"
                                checked={isEnabled}
                                color="success"
                                onChange={(e) => handleToggleSaaS(fest, e)}
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Box>
          </Paper>
        </Box>

        {/* ======================================================== */}
        {/* RIGHT COLUMN: One Unified Tree View (Granted Only)       */}
        {/* ======================================================== */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {archLoading ? (
            <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
              <CircularProgress size={36} sx={{ mb: 2, color: '#0284c7' }} />
              <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                Loading festival edition architecture and granted permissions in Tree View...
              </Typography>
            </Paper>
          ) : !architecture ? (
            <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600, mb: 1 }}>
                No Festival Selected
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Select an active SaaS festival from the left, or search to enable a new festival.
              </Typography>
            </Paper>
          ) : (
            <Box>
              {/* Selected Festival Edition Banner Card */}
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  mb: 3,
                  borderRadius: 2.5,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ffffff',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                      <SafeChip
                        label="FESTIVAL EDITION NODE"
                        size="small"
                        sx={{
                          bgcolor: '#f1f5f9',
                          color: '#475569',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          letterSpacing: 0.5,
                        }}
                      />
                      {architecture.edition.isSaaS || architecture.edition.saasEnabled ? (
                        <SafeChip
                          icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                          label="SaaS Active"
                          size="small"
                          sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }}
                        />
                      ) : (
                        <SafeChip
                          label="SaaS Inactive"
                          size="small"
                          sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }}
                        />
                      )}
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: -0.5, mb: 0.5 }}>
                      {architecture.edition.name} · {architecture.edition.edition}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Slug: <code style={{ backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{architecture.edition.slug}</code>
                      {' · '}Event ID: <code style={{ backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{architecture.edition.eventId}</code>
                      {' · '}Max Users: <b>{architecture.edition.maxUsers} seats</b>
                    </Typography>
                  </Box>

                  {/* Actions & Portal Launch */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', p: 1, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <TextField
                        size="small"
                        type="number"
                        label="Max Seats"
                        value={seatLimitInput}
                        onChange={(e) => setSeatLimitInput(e.target.value)}
                        sx={{ width: 110 }}
                        inputProps={{ min: 1, max: 1000 }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveSeatLimits}
                        disabled={savingLimits}
                        sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
                      >
                        {savingLimits ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                    </Box>

                    <Button
                      variant="contained"
                      color="error"
                      endIcon={launchingPortal ? <CircularProgress size={16} color="inherit" /> : <LaunchIcon />}
                      onClick={handleLaunchPortal}
                      disabled={launchingPortal}
                      sx={{ fontWeight: 700, textTransform: 'none', px: 2.5 }}
                    >
                      {launchingPortal ? 'Launching...' : 'Launch Festival Portal'}
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* Toolbar: Only Expand All and Collapse All */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <AccountTreeIcon sx={{ color: '#0284c7', fontSize: 22 }} />
                  <Typography variant="subtitle1" sx={{ color: '#1e293b', fontWeight: 800, letterSpacing: -0.2 }}>
                    Festival Architecture & Permissions Tree
                  </Typography>
                  <SafeChip
                    label="Granted Only"
                    size="small"
                    sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 800, fontSize: '0.72rem', border: '1px solid #fecaca' }}
                  />
                  <SafeChip
                    icon={<LockIcon sx={{ fontSize: '13px !important', color: '#64748b' }} />}
                    label="Immutable (Read-Only)"
                    size="small"
                    sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: '0.72rem' }}
                  />
                </Box>

                {/* Only Expand All and Collapse All (controls modules) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSetAllModulesExpanded(true)}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.4, px: 1.5, color: '#334155', borderColor: '#cbd5e1', fontWeight: 700 }}
                  >
                    Expand All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSetAllModulesExpanded(false)}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.4, px: 1.5, color: '#334155', borderColor: '#cbd5e1', fontWeight: 700 }}
                  >
                    Collapse All
                  </Button>
                </Box>
              </Box>

              {/* Modules List Accordion */}
              {architecture.modules.map((mod) => {
                const isCore = mod.type === 'core';
                const isAddon = mod.type === 'addon';
                const typeColor = isCore ? '#0284c7' : isAddon ? '#8b5cf6' : '#f59e0b';
                const typeBg = isCore ? '#e0f2fe' : isAddon ? '#ede9fe' : '#fef3c7';

                return (
                  <Accordion
                    key={mod.id}
                    expanded={Boolean(expandedModules[mod.id])}
                    onChange={(e, isExpanded) => handleToggleModuleAccordion(mod.id, isExpanded)}
                    sx={{
                      mb: 2.5,
                      borderRadius: '14px !important',
                      border: '1px solid #e2e8f0',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      overflow: 'hidden',
                    }}
                  >
                    {/* Accordion Summary */}
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: mod.isEnabled ? '#ffffff' : '#f8fafc',
                        borderBottom: '1px solid #f1f5f9',
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2, flexWrap: 'wrap', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <SafeChip
                            label={mod.type.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: typeBg,
                              color: typeColor,
                              fontWeight: 800,
                              fontSize: '0.65rem',
                              height: 22,
                            }}
                          />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {mod.label}
                          </Typography>
                          <SafeChip
                            label={`${mod.scopedPermissions?.length || 0} Scoped Perms`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>

                        {/* Module Provisioning Toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} onClick={(e) => e.stopPropagation()}>
                          <SafeChip
                            label={mod.isEnabled ? 'Provisioned' : 'Disabled'}
                            size="small"
                            sx={{
                              bgcolor: mod.isEnabled ? '#dcfce7' : '#f1f5f9',
                              color: mod.isEnabled ? '#15803d' : '#64748b',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                            }}
                          />
                          <Tooltip title={mod.isEnabled ? 'Deprovision module from festival' : 'Provision module for festival'}>
                            <Switch
                              size="small"
                              checked={mod.isEnabled}
                              color="primary"
                              onChange={() => handleToggleModule(mod.id, mod.isEnabled)}
                            />
                          </Tooltip>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    {/* Accordion Details */}
                    <AccordionDetails sx={{ p: 2.5, bgcolor: '#ffffff' }}>
                      {/* Module Pages Chips */}
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', mr: 1, display: 'inline-block', mb: 0.5 }}>
                          📑 Module Pages ({mod.pages && mod.pages.length > 0 ? mod.pages.length : (mod.routes?.length || 0)}):
                        </Typography>
                        <Box sx={{ display: 'inline-flex', gap: 0.8, flexWrap: 'wrap', verticalAlign: 'middle' }}>
                          {(mod.pages && mod.pages.length > 0 ? mod.pages : (mod.routes || []).map(r => ({ label: r, route: r }))).map((pg, idx) => (
                            <SafeChip
                              key={idx}
                              label={pg.label && pg.label !== pg.route ? `${pg.label} · ${pg.route}` : (pg.route || pg)}
                              size="small"
                              sx={{ mr: 0.8, fontSize: '0.75rem', bgcolor: '#e2e8f0', color: '#1e293b', fontWeight: 600 }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* One Unified Tree Container for this Module */}
                      {(!mod.scopedPermissions || mod.scopedPermissions.length === 0) ? (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          This module does not have granular permissions. Access is governed by module presence.
                        </Alert>
                      ) : (
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#ffffff', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Inner Module Header with Role Groups & Expand/Collapse Groups controls */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              pb: 1.5,
                              borderBottom: '1px solid #f1f5f9',
                              flexWrap: 'wrap',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>
                                Role Groups & Scoped Permissions ({architecture.groups?.length || 0} Groups)
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSetModuleGroupsExpanded(mod.id, true)}
                                sx={{
                                  fontSize: '0.72rem',
                                  textTransform: 'none',
                                  py: 0.25,
                                  px: 1.2,
                                  color: '#0284c7',
                                  borderColor: '#bae6fd',
                                  bgcolor: '#f0f9ff',
                                  fontWeight: 700,
                                  borderRadius: '6px',
                                  '&:hover': { bgcolor: '#e0f2fe', borderColor: '#7dd3fc' },
                                }}
                              >
                                Expand Groups
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSetModuleGroupsExpanded(mod.id, false)}
                                sx={{
                                  fontSize: '0.72rem',
                                  textTransform: 'none',
                                  py: 0.25,
                                  px: 1.2,
                                  color: '#64748b',
                                  borderColor: '#cbd5e1',
                                  bgcolor: '#f8fafc',
                                  fontWeight: 700,
                                  borderRadius: '6px',
                                  '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
                                }}
                              >
                                Collapse Groups
                              </Button>
                            </Box>
                          </Box>

                          {architecture.groups.map((grp, grpIdx) => {
                            const grpSet = matrixState[mod.id]?.[grp.key] || new Set();

                            // Filter to ONLY granted permissions
                            const grantedPerms = (mod.scopedPermissions || []).filter(
                              p => grpSet.has(p.key) || grpSet.has(p.short_key) || grpSet.has('*')
                            );

                            const grantedDomains = groupScopedPermissionsByDomain(grantedPerms, mod.moduleKey);
                            const groupNodeKey = `${mod.id}:${grp.key}`;
                            const isGroupExpanded = expandedNodes[groupNodeKey] !== false;
                            const isLastGroup = grpIdx === architecture.groups.length - 1;
                            const meta = GROUP_METAS[grp.key] || { label: grp.label, icon: '👥' };

                            return (
                              <Box
                                key={grp.key}
                                sx={{
                                  borderBottom: isLastGroup ? 'none' : '1px solid #f1f5f9',
                                  pb: isLastGroup ? 0 : 2,
                                }}
                              >
                                {/* Group Level Node */}
                                <Box
                                  onClick={() => handleToggleNode(groupNodeKey)}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.2,
                                    px: 1.5,
                                    borderRadius: 2,
                                    bgcolor: '#f8fafc',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    border: '1px solid #e2e8f0',
                                    '&:hover': { bgcolor: '#f1f5f9' },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        minWidth: 18,
                                        bgcolor: '#dc2626',
                                        color: '#ffffff',
                                        borderRadius: '3px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 800,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {isGroupExpanded ? '−' : '+'}
                                    </Box>
                                    <Typography sx={{ fontSize: '1.1rem' }}>{meta.icon}</Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                      {grp.label}
                                    </Typography>
                                  </Box>

                                  <SafeChip
                                    label={`${grantedPerms.length} Granted`}
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      bgcolor: grantedPerms.length > 0 ? '#fef2f2' : '#f1f5f9',
                                      color: grantedPerms.length > 0 ? '#dc2626' : '#64748b',
                                      border: '1px solid',
                                      borderColor: grantedPerms.length > 0 ? '#fecaca' : '#e2e8f0',
                                    }}
                                  />
                                </Box>

                                {/* Group Children (Domains & Permissions in One Continuous Tree) */}
                                {isGroupExpanded && (
                                  <Box sx={{ mt: 1.2, ml: '10px', pl: '16px', borderLeft: '2px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {grantedPerms.length === 0 ? (
                                      <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', py: 1 }}>
                                        No permissions granted for {grp.label} in {mod.label}.
                                      </Typography>
                                    ) : (
                                      grantedDomains.map((dom) => {
                                        const domainNodeKey = `${mod.id}:${grp.key}:${dom.domain}`;
                                        const isDomainExpanded = expandedNodes[domainNodeKey] !== false;

                                        return (
                                          <Box key={dom.domain} sx={{ display: 'flex', flexDirection: 'column' }}>
                                            {/* Domain Node */}
                                            <Box
                                              onClick={() => handleToggleNode(domainNodeKey)}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                py: 0.6,
                                                px: 1,
                                                borderRadius: 1.5,
                                                bgcolor: '#ffffff',
                                                border: '1px solid #e2e8f0',
                                                '&:hover': { bgcolor: '#f8fafc' },
                                              }}
                                            >
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                  component="span"
                                                  sx={{
                                                    width: 16,
                                                    height: 16,
                                                    minWidth: 16,
                                                    bgcolor: '#dc2626',
                                                    color: '#ffffff',
                                                    borderRadius: '3px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 11,
                                                    fontWeight: 800,
                                                    lineHeight: 1,
                                                  }}
                                                >
                                                  {isDomainExpanded ? '−' : '+'}
                                                </Box>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 800,
                                                    fontFamily: 'JetBrains Mono, Menlo, monospace',
                                                    color: '#0f172a',
                                                    fontSize: '0.82rem',
                                                  }}
                                                >
                                                  {dom.icon} {dom.domain}
                                                </Typography>
                                              </Box>
                                              <SafeChip
                                                label={`${dom.permissions.length} perms`}
                                                size="small"
                                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }}
                                              />
                                            </Box>

                                            {/* Permission Items in One Tree (NO Red Ticks) */}
                                            {isDomainExpanded && (
                                              <Box sx={{ ml: '8px', pl: '14px', borderLeft: '2px solid #e2e8f0', mt: 0.8, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                                {dom.permissions.map((p) => (
                                                  <Box
                                                    key={p.id || p.key}
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'flex-start',
                                                      gap: 1,
                                                      py: 0.4,
                                                    }}
                                                  >
                                                    {/* Tree Connector Bullet (No Red Tick) */}
                                                    <Typography
                                                      component="span"
                                                      sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.9rem',
                                                        color: '#94a3b8',
                                                        lineHeight: 1.2,
                                                        mt: '1px',
                                                        userSelect: 'none',
                                                        fontWeight: 700,
                                                      }}
                                                    >
                                                      -
                                                    </Typography>

                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                      <Typography
                                                        variant="body2"
                                                        sx={{
                                                          fontFamily: 'JetBrains Mono, Menlo, monospace',
                                                          fontWeight: 700,
                                                          fontSize: '0.8125rem',
                                                          color: '#0f172a',
                                                          lineHeight: 1.3,
                                                        }}
                                                      >
                                                        {p.key}
                                                      </Typography>
                                                      <Typography
                                                        variant="caption"
                                                        sx={{ color: '#64748b', fontSize: '0.72rem', display: 'block', mt: 0.2 }}
                                                      >
                                                        {p.label}
                                                        {p.description && p.description !== p.label ? ` · ${p.description}` : ''}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                ))}
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Feedback Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
